import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  TextField,
  Typography
} from '@mui/material';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useUserContextController } from '../../../context/UserContext';
import { secondaryActionButtonSx } from '../../modal/ModalActionButtons';
import {
  listHubspotSegments,
  getHubspotSegmentMembers,
  HubspotSegment
} from '../../../api/connector/hubspot';

interface Props {
  onConfirm: (rows: any[]) => void;
  onCancel: () => void;
  hideCancel?: boolean;
}

const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : 'N/A');

const ImportHubspotSegments: React.FC<Props> = ({
  onConfirm,
  onCancel,
  hideCancel = false
}) => {
  const { showSnackbar } = useSnackbar();
  const [userContext] = useUserContextController();
  const hubspotConnection = useMemo(
    () => userContext?.connectedAccounts?.find((c: any) => c.app === 'hubspot'),
    [userContext]
  );
  const connectionId = hubspotConnection?.connectionId as string | undefined;

  const [segments, setSegments] = useState<HubspotSegment[]>([]);
  const [currentOffset, setCurrentOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSegment, setSelectedSegment] = useState<HubspotSegment | null>(
    null
  );

  const fetchSegments = async (query: string, offset?: number) => {
    const {
      records,
      offset: newOffset,
      hasMore
    } = await listHubspotSegments({
      query,
      offset
    });
    return { records, newOffset, hasMore };
  };

  useEffect(() => {
    const bootstrap = async () => {
      if (!connectionId) return;
      setLoadingList(true);
      try {
        const { records, newOffset, hasMore } =
          await fetchSegments(searchQuery);
        setSegments(records || []);
        setCurrentOffset(newOffset || 0);
        setHasMore(hasMore || false);
      } catch (e: any) {
        showSnackbar(e?.message || 'Failed to list HubSpot segments', 'error');
      } finally {
        setLoadingList(false);
        setInitialLoadDone(true);
      }
    };
    bootstrap();
  }, [connectionId, showSnackbar]);

  const handleSearch = async () => {
    if (!connectionId) return;
    setLoadingAction(true);
    try {
      const { records, newOffset, hasMore } = await fetchSegments(searchQuery);
      setSegments(records || []);
      setCurrentOffset(newOffset || 0);
      setHasMore(hasMore || false);
    } catch (e: any) {
      showSnackbar(e?.message || 'Failed to search segments', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const refresh = async () => {
    if (!connectionId) return;
    setLoadingAction(true);
    try {
      const { records, newOffset, hasMore } = await fetchSegments(searchQuery);
      setSegments(records || []);
      setCurrentOffset(newOffset || 0);
      setHasMore(hasMore || false);
    } catch (e: any) {
      showSnackbar(e?.message || 'Failed to refresh segments', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const loadMore = async () => {
    if (!hasMore) return;
    setLoadingAction(true);
    try {
      const nextOffset = segments.length;
      const {
        records,
        newOffset,
        hasMore: moreAvailable
      } = await fetchSegments(searchQuery, nextOffset);
      setSegments((prev) => [...prev, ...(records || [])]);
      setCurrentOffset(newOffset || 0);
      setHasMore(moreAvailable || false);
    } catch (e: any) {
      showSnackbar(e?.message || 'Failed to load more segments', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleSelectSegment = (segment: HubspotSegment) => {
    setSelectedSegment(segment);
  };

  const handleFetchSegment = async () => {
    if (!selectedSegment) return;
    setLoadingAction(true);
    try {
      const members = await getHubspotSegmentMembers({
        segmentId: selectedSegment.listId,
        limit: 500
      });

      if (!members || members.length === 0) {
        showSnackbar('This segment has no members.', 'info');
        return;
      }

      showSnackbar(`Found ${members.length} prospects!`, 'success');

      const flattenedMembers = members.map((m: any) => m.properties || m);
      onConfirm(flattenedMembers);
    } catch (e: any) {
      showSnackbar(e?.message || 'Failed to fetch segment members', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const renderSkeletonList = () => (
    <List
      dense
      sx={{
        maxHeight: 280,
        overflow: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1
      }}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <Box key={i} px={2} py={1.25}>
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="40%" />
          {i < 5 && <Divider sx={{ mt: 1 }} />}
        </Box>
      ))}
    </List>
  );

  const renderEmptyState = () => (
    <Box
      sx={{
        flex: 1,
        textAlign: 'center',
        height: '100px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Typography variant="subtitle1">No segments found</Typography>
    </Box>
  );

  const renderSegmentList = () => (
    <Box>
      <List
        dense
        sx={{
          maxHeight: 280,
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        {segments.map((segment, idx) => (
          <React.Fragment key={segment.listId}>
            <ListItemButton
              disabled={loadingAction}
              onClick={() => handleSelectSegment(segment)}
              selected={selectedSegment?.listId === segment.listId}
            >
              <ListItemText
                primary={segment.name}
                secondary={`Updated: ${fmt(segment.updatedAt)}`}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItemButton>
            {idx < segments.length - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
        {segments.length === 0 && initialLoadDone && renderEmptyState()}
      </List>
      {hasMore && (
        <Box display="flex" justifyContent="center" mt={1}>
          <Button
            onClick={loadMore}
            disabled={loadingAction || loadingList}
            sx={{
              textTransform: 'capitalize',
              color: 'primary.main'
            }}
          >
            {loadingAction ? <CircularProgress size={18} /> : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="body2" color="text.secondary">
        Select a segment to import prospects from.
      </Typography>

      <Box display="flex" gap={1}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search segments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
          disabled={loadingAction || loadingList}
        />
        <Button
          variant="outlined"
          onClick={handleSearch}
          disabled={loadingAction || loadingList}
          sx={{
            textTransform: 'capitalize',
            minWidth: '80px'
          }}
        >
          {loadingAction ? <CircularProgress size={20} /> : 'Search'}
        </Button>
      </Box>

      {loadingList ? renderSkeletonList() : renderSegmentList()}

      <Box display="flex" gap={1} justifyContent="flex-end">
        {!hideCancel && (
          <Button
            variant="text"
            onClick={onCancel}
            disabled={loadingAction}
            sx={secondaryActionButtonSx}
          >
            Back
          </Button>
        )}
        <Button
          onClick={handleFetchSegment}
          variant="contained"
          disabled={!selectedSegment || loadingAction}
          sx={(theme) => ({
            border: `1px solid ${theme.customColors.themePrimary}`,
            backgroundColor: theme.customColors.themePrimary,
            color: theme.customColors.themeSecondary,
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:disabled': { borderColor: 'inherit' }
          })}
        >
          {loadingAction ? <CircularProgress size={18} /> : 'Fetch'}
        </Button>
      </Box>
    </Box>
  );
};

export default ImportHubspotSegments;
